import React from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import HomePage from './pages/HomePage';
import CatalogPage from './pages/CatalogPage';
import VillaPage from './pages/VillaPage';
import ContactPage from './pages/ContactPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboard from './pages/AdminDashboard';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

import './App.css';

// Supported languages
const SUPPORTED_LANGS = ['de', 'pl', 'sv', 'cs'];

// Wrapper to handle language from URL
const LangWrapper = ({ children }) => {
  const { lang } = useParams();
  const { setLanguage } = useLanguage();
  
  React.useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang)) {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);
  
  return children;
};

// Main layout with Navbar and Footer
const MainLayout = ({ children }) => (
  <div className="min-h-screen flex flex-col" data-testid="app-container">
    <Navbar />
    <main className="flex-1">
      {children}
    </main>
    <Footer />
    <Toaster position="bottom-right" />
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Admin routes - no layout */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin/*" element={<AdminDashboard />} />
              
              {/* Language-prefixed routes */}
              <Route path="/:lang" element={<LangWrapper><MainLayout><HomePage /></MainLayout></LangWrapper>} />
              <Route path="/:lang/catalog" element={<LangWrapper><MainLayout><CatalogPage /></MainLayout></LangWrapper>} />
              <Route path="/:lang/villa/:id" element={<LangWrapper><MainLayout><VillaPage /></MainLayout></LangWrapper>} />
              <Route path="/:lang/contact" element={<LangWrapper><MainLayout><ContactPage /></MainLayout></LangWrapper>} />
              
              {/* Default routes (English) */}
              <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
              <Route path="/catalog" element={<MainLayout><CatalogPage /></MainLayout>} />
              <Route path="/villa/:id" element={<MainLayout><VillaPage /></MainLayout>} />
              <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
